import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { SuppressionSummary } from '@shared/schemas/email';
import type {
  NotificationRecipientInput,
  NotificationRecipientSummary,
  UpdateNotificationRecipientInput,
} from '@shared/schemas/notification-email';

/*
 * The queries behind /admin/notification-emails — §11.3 and §8.2's suppression
 * list, which share a screen because they are two halves of "who gets our mail
 * and who does not".
 *
 * Every mutation invalidates the whole root rather than patching the cache:
 * adding an address that already exists switched off *reactivates* it rather
 * than inserting, so what comes back is not always the row the caller thought
 * they were creating.
 */

const ROOT = ['admin', 'email'] as const;

export function useNotificationRecipients() {
  return useQuery({
    queryKey: [...ROOT, 'recipients'],
    queryFn: () =>
      api.get<{ items: NotificationRecipientSummary[] }>('/api/admin/email/recipients'),
  });
}

export function useAddRecipient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: NotificationRecipientInput) =>
      api.post<NotificationRecipientSummary>('/api/admin/email/recipients', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useUpdateRecipient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateNotificationRecipientInput }) =>
      api.patch<NotificationRecipientSummary>(`/api/admin/email/recipients/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useRemoveRecipient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/admin/email/recipients/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}

export function useSuppressions() {
  return useQuery({
    queryKey: [...ROOT, 'suppressions'],
    queryFn: () => api.get<{ items: SuppressionSummary[] }>('/api/admin/email/suppressions'),
  });
}

export function useLiftSuppression() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) =>
      api.post<{ email: string; lifted: boolean }>('/api/admin/email/suppressions/lift', {
        email,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ROOT }),
  });
}
